import React from 'react';
import {Button, IconAdd, SectionPage} from '@snsw-gel/react';
import CannotLinkPersonalOrBusinessAlert from './CannotLinkPersonalOrBusinessAlert';
import {Bold} from '../../components/Bold';
import {displayableTollProvider} from '../../utils/helpers';
import TollProvider from '../../types/TollProvider';
import ClaimBeingProcessedAlert from './ClaimBeingProcessedAlert';
import {FeatureToggleName, useBaseFeature} from '../../hooks/features/useBaseFeature';

interface PersonalProps {
    readonly weeklyCap: string;
    readonly tollProvider?: TollProvider;
    readonly tollAccountNumber?: string;
    readonly hasMixed: boolean;
    readonly onUnlink: () => void;
    readonly onLink: () => void;
    readonly processingClaimsBlocksUnlinking: boolean;
    readonly isTr2Decommissioned: boolean;
}

/**
 * The personal account section of the MTA page
 */
const Personal: React.FC<PersonalProps> = (
    {
        weeklyCap,
        tollProvider,
        tollAccountNumber,
        hasMixed,
        onLink,
        onUnlink,
        processingClaimsBlocksUnlinking,
        isTr2Decommissioned,
    }: PersonalProps
) => {
    const hasLinkedPersonalTollAccount = tollProvider !== undefined && tollAccountNumber !== undefined;

    return <>
        {!isTr2Decommissioned ?
            <>
            <h2>For personal claims only</h2>
            <SectionPage>
                {processingClaimsBlocksUnlinking && ClaimBeingProcessedAlert}
                <p>Personal customers / motorists can claim from:</p>
                <ul>
                    <li>{weeklyCap} weekly rebate and</li>
                    <li>40% rebate schemes.</li>
                </ul>
                <p>
                    If you use your toll account for personal tolls only, you can link one personal toll account.
                </p>
            </SectionPage>
            </> :
            <>
            <h2>Personal toll account</h2>
            <SectionPage>
                {processingClaimsBlocksUnlinking && ClaimBeingProcessedAlert}
                <p>Individuals with personal toll accounts can claim under the $60 weekly rebate. You can only link one personal toll account.
                    If you have multiple accounts, choose the one you use most often.
                </p>
            </SectionPage>
            </>
        }
        <SectionPage>
            {!isTr2Decommissioned && <h3>Personal toll account</h3> }

            {isTr2Decommissioned && hasLinkedPersonalTollAccount && <h3>Toll account details</h3> }

            {/*TODO remove after tr2 decommissioning*/}
            {hasMixed && CannotLinkPersonalOrBusinessAlert}

            {hasLinkedPersonalTollAccount ? (
                // Linked account
                <>
                    <p>
                        Toll provider: <Bold>{displayableTollProvider(tollProvider)}</Bold>
                    </p>
                    <p>
                        Toll account number: <Bold>{tollAccountNumber}</Bold>
                    </p>
                    {!isTr2Decommissioned &&
                        <p>
                            <Bold>Note: </Bold>
                            All personal claims, on any toll account, count towards your maximum rebate amount.
                        </p>
                    }
                    <Button variant='secondary' onClick={onUnlink} id='pers-unlink'>
                        Remove personal toll account
                    </Button>
                </>
            ) : (
                // No linked account
                <Button variant='secondary' iconStart={<IconAdd/>} onClick={onLink} id='link-pers'>
                    Link personal toll account
                </Button>
            )}
        </SectionPage>
    </>;
};

export default Personal;
